import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import type { PropsWithChildren } from 'react';

import type { Message, Advertisement } from '../braze-consumer/braze-consumer';
import { publishEvents, subscribeEvents } from '../events';
import type { Placement } from '../placements';
import placements from '../placements';
import { hasFrequencyCapping } from '../utils/frequency-capping';
import { Logger } from '../utils/logger';
import type { TrackingLibrary, ContextData } from '../utils/tracking';
import {
  getTrackingLibrary,
  setupTracking,
  trackEvent,
} from '../utils/tracking';

import type { ContentCardState, ContentCard } from './content-card-provider';
import { ContentCardProvider } from './content-card-provider';

type SubscribeConsumer = (
  type: string,
  callback: (ad: Advertisement | Advertisement[], section: string) => void
) => void;

export type TransportLayerPayloadType = {
  type?: string;
  campaignId?: string;
  value?: any;
  buttonId?: string;
};

export interface TransportLayer {
  subscribe: (
    action: string,
    handleIncomingMessage: (message: Message) => void
  ) => Promise<() => void>;
  publish: (
    action: string,
    payload: TransportLayerPayloadType,
    options: { fireAndForget: boolean }
  ) => void;
  event: (
    action: 'brazeImpression' | 'brazeClick' | 'brazeClose',
    contextData: ContextData
  ) => void;
}

interface BrazeContextProps {
  subscribeConsumer: SubscribeConsumer;
  trackingLibrary: TrackingLibrary;
}

const BrazeContext = React.createContext<Partial<BrazeContextProps>>({});

interface BrazeProviderProps {
  transportLayer: TransportLayer;
  section: string;
}

const BrazeProvider: React.FC<PropsWithChildren<BrazeProviderProps>> = ({
  children,
  transportLayer,
  section,
}) => {
  // List of consumers subscribed.
  const consumers = new Map<
    string,
    Set<(ad: Advertisement, section: string) => void>
  >();
  const unsubscribe = useRef(() => {});

  const trackingLibrary = getTrackingLibrary(transportLayer);

  const [contentCards, setContentCards] = useState<ContentCardState>({
    status: 'waiting',
    cards: [],
  });

  const subscribeConsumer: SubscribeConsumer = (type, callback) => {
    if (!consumers.has(type)) {
      consumers.set(type, new Set());
    }
    consumers.get(type)?.add(callback);

    return () => {
      const consumer = consumers.get(type);
      if (consumer) {
        consumer.delete(callback);
        if (consumer.size === 0) {
          consumers.delete(type);
        }
      }
    };
  };

  const isMatchingPlacement = (type: string, placement: Placement): boolean => {
    switch (type) {
      case subscribeEvents.IN_APP_MESSAGE: {
        // Weird typecast? Have a look at https://github.com/microsoft/TypeScript/issues/26255 ...
        const iamPlacements: string[] = [
          placements.PRESCREEN,
          placements.POP_OVER,
          placements.SLIDE_UP,
        ];
        return iamPlacements.includes(placement);
      }
      case subscribeEvents.CONTENT_CARDS:
        return placement === placements.TEASER;
      default:
        return false;
    }
  };

  const hasMatchingSection = (messageSection: string): boolean =>
    [section, 'all'].includes(messageSection) ||
    (messageSection === undefined && section === 'wbm/Startpage_LI'); // Legacy case

  const isEligibleInAppMessage = (iam: Advertisement): boolean => {
    const frequencyCapped = hasFrequencyCapping(
      section,
      iam.extras?.placement,
      iam?.extras?.ignore_capping
    );
    if (frequencyCapped) {
      Logger.log(`BrazeProvider - frequencyCapping applied`);
      return false;
    }
    return isMatchingPlacement(
      subscribeEvents.IN_APP_MESSAGE,
      iam.extras?.placement
    );
  };

  const isEligibleCardMessage = (
    card: Advertisement,
    cardType: string
  ): boolean => {
    return (
      hasMatchingSection(card.extras?.section) &&
      isMatchingPlacement(cardType, card.extras?.placement)
    );
  };

  const isControlCardMessage = (card: Advertisement): string | undefined => {
    return card.extras && card.extras.control;
  };

  const trackControlMessageImpression = (
    triggerId: string | undefined
  ): void => {
    transportLayer.publish(
      'stm:braze',
      { type: publishEvents.IN_APP_MESSAGE_SHOWN, campaignId: triggerId },
      { fireAndForget: true }
    );
    setupTracking(transportLayer);
    trackEvent('brazeImpression', {
      brazePlacement: placements.CONTROL_MESSAGE,
      brazeTriggerID: triggerId,
    });
  };

  const trackControlCardImpression = (triggerId: string | undefined): void => {
    transportLayer.publish(
      'stm:braze',
      { type: publishEvents.CONTENT_CARD_SHOWN, campaignId: triggerId },
      { fireAndForget: true }
    );
    setupTracking(transportLayer);
    trackEvent('brazeImpression', {
      brazePlacement: placements.CONTROL_MESSAGE,
      brazeTriggerID: triggerId,
    });
  };

  const handleIncomingMessage = (message: Message): void => {
    Logger.log('BrazeProvider - incoming message', message);
    let selectedAd;

    if (message.type === subscribeEvents.IN_APP_MESSAGE) {
      const iam = message.data as Advertisement;
      if (iam.isControlMessage) {
        trackControlMessageImpression(iam.triggerId);
      }
      selectedAd = isEligibleInAppMessage(iam) ? iam : undefined;
    } else if (message.type === subscribeEvents.CONTENT_CARDS) {
      const cards = message.data as Advertisement[];
      setContentCards({
        status: 'received',
        cards: message.data as unknown as ContentCard[],
      });
      const incomingAds = cards.filter((card) => {
        return isEligibleCardMessage(card, message.type);
      });
      selectedAd = incomingAds[Math.floor(Math.random() * incomingAds.length)];
      if (selectedAd && isControlCardMessage(selectedAd)) {
        trackControlCardImpression(selectedAd.id);
        selectedAd = null;
      }
    }

    if (selectedAd) {
      Logger.log('BrazeProvider - selectedAd', selectedAd);
      consumers.get(selectedAd.extras.placement)?.forEach((cb) => {
        return cb(selectedAd, section);
      });
    }
  };

  useEffect(() => {
    const subscribeTransportLayer = async (): Promise<void> => {
      unsubscribe.current = await transportLayer.subscribe(
        'stm:braze',
        handleIncomingMessage
      );
    };
    subscribeTransportLayer();

    return () => {
      unsubscribe.current();
    };
  }, []);

  return (
    <BrazeContext.Provider value={{ subscribeConsumer, trackingLibrary }}>
      <ContentCardProvider contentCardState={contentCards}>
        {children}
      </ContentCardProvider>
    </BrazeContext.Provider>
  );
};

export { BrazeProvider, BrazeContext };
